/**
 * PlyxOrderSuccess — buyer landing page after Stripe Checkout success.
 *
 * Route: `/plyx/order/success?session_id=cs_...`
 *
 * Polls our backend for the order doc until the webhook lands and the
 * status flips to "paid". Shows a confetti hero + receipt summary.
 *
 * We don't trust the URL alone — payment_status is read from MongoDB
 * which is only updated by the verified Stripe webhook.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { Loader2, CheckCircle2, ShoppingBag, ArrowLeft, Sparkles } from "lucide-react";
import { playPlyxChime } from "@/lib/plyxChime";
import { celebratePlyx } from "@/components/plyx/PlyxCelebration";

const PlyxOrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);

  // Find the order via session_id, then poll until the webhook flips
  // status to "paid". We give it 30s before showing "still confirming…"
  // — Stripe usually fires within 1-2s in test mode.
  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    let timer;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await plyxApi.get(`/plyx/shop/session/${sessionId}`);
        setOrder(data);
        if (data.status === "paid" || attempts > 30) {
          if (data.status === "paid") {
            // Celebrate the conversion — chime + visual burst.
            playPlyxChime({ volume: 0.7 });
            celebratePlyx({ message: "Order confirmed ✨" });
          }
          setPolling(false);
          setLoading(false);
          return;
        }
      } catch {
        if (attempts > 5) { setPolling(false); setLoading(false); return; }
      }
      timer = setTimeout(tick, 1500);
    };
    tick();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-order-success-page">
      <div className="max-w-md mx-auto px-5 pt-10 pb-24">
        <Link to="/plyx" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4"/> back to Plyx
        </Link>

        {!sessionId ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
            <div className="font-semibold mb-1">Missing order reference</div>
            <p className="text-sm text-white/65">
              We can&apos;t find the checkout session you came from. Try again from the seller&apos;s shop.
            </p>
          </div>
        ) : loading || (polling && order?.status !== "paid") ? (
          <div className="text-center py-12" data-testid="plyx-order-success-confirming">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#FFB42D] mb-4"/>
            <div className="font-display text-2xl mb-1">Confirming your payment…</div>
            <p className="text-sm text-white/55">Should only take a second.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8" data-testid="plyx-order-success-confirmed">
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ background: "#E85D4E" }}
              >
                <CheckCircle2 className="w-10 h-10 text-black" strokeWidth={2.4}/>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[#FFB42D] mb-2">
                <Sparkles className="w-4 h-4"/>
                <span className="text-xs uppercase tracking-wider font-semibold">Order confirmed</span>
                <Sparkles className="w-4 h-4"/>
              </div>
              <h1 className="font-display text-3xl tracking-tight">Thanks for shopping on Plyx</h1>
              <p className="text-sm text-white/65 mt-2">
                We&apos;ve notified the seller. Expect an email with shipping details shortly.
              </p>
            </div>

            {order && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3 mb-4" data-testid="plyx-order-receipt">
                <div className="flex items-start gap-3">
                  <ShoppingBag className="w-5 h-5 text-[#FFB42D] mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{order.listing_title || "Plyx product"}</div>
                    <div className="text-xs text-white/55">Qty {order.qty} · Order {order.order_id}</div>
                  </div>
                </div>
                <div className="border-t border-white/8 pt-3 text-sm flex items-baseline justify-between">
                  <span className="text-white/55">Total paid</span>
                  <span className="font-display text-2xl text-[#FFB42D] tabular-nums">
                    {order.currency === "GBP" ? "£" : order.currency === "USD" ? "$" : order.currency === "EUR" ? "€" : `${order.currency} `}
                    {Number(order.amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <Link
              to="/plyx"
              data-testid="plyx-order-success-back-feed"
              className="mt-4 block text-center h-11 leading-[44px] rounded-full font-semibold text-black"
              style={{ background: "#E85D4E" }}
            >
              Keep watching Plyx
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PlyxOrderSuccess;
